import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { promptsApi } from '../../utils/promptsApi';
import DizeeInput from '../../components/DizeeComponent/DizeeInput';



function CreatePrompt() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: '',
    category: 'general'
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEditing && id) {
      const fetchPrompt = async () => {
        try {
          setLoading(true);
          const prompt = await promptsApi.getPromptById(id);
          setFormData({
            title: prompt.title || '',
            description: prompt.description || '',
            content: prompt.content || '',
            category: prompt.category || 'general'
          });
        } catch (error) {
          console.error('Error fetching prompt:', error);
          // Handle error - maybe redirect to prompts list
        } finally {
          setLoading(false);
        }
      };

      fetchPrompt();
    }
  }, [isEditing, id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      let result;
      if (isEditing && id) {
        result = await promptsApi.updatePrompt(id, formData);
        console.log('✅ Prompt updated:', result);
      } else {
        result = await promptsApi.createPrompt(formData);
        console.log('✅ Prompt created:', result);
      }
      
      // Show success message briefly before navigating
      console.log('🎉 Success! Navigating back to prompts...');
      
      // Small delay to see the success in console, then navigate
      setTimeout(() => {
        navigate('/prompts');
      }, 1000);
      
    } catch (error) {
      console.error('❌ Error saving prompt:', error);
      // TODO: Show error message to user
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/prompts');
  };

  if (loading) {
    return (
      <LayoutHeader>
        <div className="flex justify-center w-full">
          <div className="w-[390px] h-[80vh] flex flex-col items-center">
            <div className="text-white text-center py-[50px]">Loading prompt...</div>
          </div>
        </div>
      </LayoutHeader>
    );
  }

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between">
            <p className="text-white text-[12px]">{isEditing ? 'Edit Prompt' : 'Create Prompt'}</p>
            <div className="flex gap-[30px]">
              <div
                className={`text-white cursor-pointer text-[12px] hover:text-gray-400 transition-colors ${formData.title && formData.content && !saving
                  ? 'opacity-100' 
                  : 'opacity-50 cursor-not-allowed'
                }`}
                onClick={() => {
                  if (formData.title && formData.content && !saving) {
                    handleSubmit({ preventDefault: () => {} });
                  }
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </div>
              <div 
                className="text-white cursor-pointer text-[12px] hover:text-gray-400 transition-colors"
                onClick={handleCancel}
              >
                Cancel
              </div>
            </div>
          </div>

          {/* Form Content */}
          <div className="w-full px-4 flex-1 overflow-y-auto pb-[100px]">
            <div className="space-y-4">
              <div>
                <label className="text-white text-[12px] mb-2 block">Title</label>
                <DizeeInput
                  placeholder="Enter a descriptive title for your prompt"
                  value={formData.title}
                  onChange={(e) => handleInputChange({ target: { name: 'title', value: e.target.value }})}
                />
              </div>

              <div>
                <label className="text-white text-[12px] mb-2 block">Description (Optional)</label>
                <DizeeInput
                  placeholder="Brief description of what this prompt does"
                  value={formData.description}
                  onChange={(e) => handleInputChange({ target: { name: 'description', value: e.target.value }})}
                />
              </div>

              <div>
                <label className="text-white text-[12px] mb-2 block">Prompt Content</label>
                <textarea
                  placeholder="Write your custom prompt here... Be specific and clear about what you want the AI to do."
                  value={formData.content}
                  onChange={(e) => handleInputChange({ target: { name: 'content', value: e.target.value }})}
                  className="w-full p-3 bg-transparent border border-[rgba(255,255,255,0.1)] rounded-lg text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] min-h-[120px] resize-none focus:outline-none focus:border-[rgba(255,255,255,0.3)]"
                />
              </div>

              {/* Preview */}
              {formData.content && (
                <div className="mt-4 p-4 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg">
                  <h3 className="text-white text-[12px] font-medium mb-2">Preview</h3>
                  <div className="text-[rgba(255,255,255,0.8)] text-[12px] leading-relaxed whitespace-pre-wrap">
                    {formData.content}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Help Text with Black Overlay */}
          <div className="w-full absolute bottom-0 left-0">
            <div className="bg-black px-4 pt-8 pb-[27px]">
              <div className="text-[12px] text-[rgba(255,255,255,0.5)] leading-[1.5] max-w-full">
                <p>Create custom prompts to streamline your AI conversations.</p>
                <p>Be specific and clear about what you want the AI to do for best results.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </LayoutHeader>
  );
}

export default CreatePrompt; 